import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Comunicacion, Plantilla as PlantillaApi, Formulario } from "../../../../api";
import { Cita as CitaApi } from "../../../../api/cita";
import { ModalCustom } from "../../../Shared/ModalCustom/ModalCustom";
import { useAuth, useUI } from "../../../../hooks";

const comunicacionAPI = new Comunicacion();
const plantillaAPI    = new PlantillaApi();
const formularioAPI   = new Formulario();
const citaAPI         = new CitaApi();

// Transiciones válidas del pipeline — espejo de GestionHumanaDetails
const VALID_TRANSITIONS_CP = {
  nuevo:            ["revision"],
  revision:         ["seleccionado", "descartado"],
  seleccionado:     ["contratado", "archivado", "descartado", "revision"],
  entrevistado:     ["revision"],   // legacy
  contratado:       [],
  descartado:       [],
  archivado:        [],
  despedido:        [],
  desahucio:        [],
  renuncia:         [],
  contrato_vencido: [],
};

// Tipos de plantilla que disparan un cambio de estado (solo aplica a EMAIL)
// optional: true → el usuario elige si enviar o solo cambiar estado
const TIPO_ACCION = {
  acuse_recibo:          { accion: "revision",    optional: false },
  invitacion_entrevista: { accion: "seleccionado", optional: false },
  bienvenida:            { accion: "contratado",   optional: false },
  rechazo_cv:            { accion: "descartado",   optional: true  },
  rechazo_entrevista:    { accion: "descartado",   optional: true  },
  banco_talentos:        { accion: "archivado",    optional: true  },
  // oferta_laboral: sin entrada — es informacional, no cambia estado
};

const ACCION_LABEL = {
  revision:    "En Revisión",
  entrevistado:"Entrevistado",
  seleccionado:"Seleccionado",
  contratado:  "Contratado",
  descartado:  "Descartado",
  archivado:   "Archivado",
};

const CANAL_ICON  = { EMAIL: "✉️", WHATSAPP: "💬", TELEGRAM: "✈️" };
const CANAL_COLOR = {
  EMAIL:    "bg-[#151C84] hover:bg-[#0d125c]",
  WHATSAPP: "bg-[#25D366] hover:bg-[#128C7E]",
  TELEGRAM: "bg-[#0088cc] hover:bg-[#0077b5]",
};

// Variables dinámicas que requieren un selector (no se auto-completan del candidato)
const VARS_PICKER = ["fecha", "hora", "lugar"];

// Dirección por defecto de la oficina — actualizar con la dirección real
const OFICINA_DIR = "Inversiones Catelli, Santo Domingo, Rep. Dom.";

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatFecha(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("es-DO", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function formatHora(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "p.m." : "a.m.";
  return `${hour % 12 || 12}:${m} ${ampm}`;
}

// ── Calendar helpers ──────────────────────────────────────────────────────────

function inferTipoCita(plantillaTipo) {
  if (["invitacion_entrevista", "recordatorio"].includes(plantillaTipo)) return "entrevista";
  if (plantillaTipo === "bienvenida") return "onboarding";
  if (plantillaTipo === "prueba_tecnica") return "prueba_tecnica";
  return "otro";
}

function dtStr(fecha, hora) {
  // fecha: "2026-06-19", hora: "10:00" → "20260619T100000"
  return `${fecha.replace(/-/g, "")}T${hora.replace(":", "")}00`;
}

function dtStrEnd(fecha, hora) {
  const [h, m] = hora.split(":").map(Number);
  const endH = String((h + 1) % 24).padStart(2, "0");
  return `${fecha.replace(/-/g, "")}T${endH}${String(m).padStart(2, "0")}00`;
}

function buildGoogleLink(fecha, hora, lugar, titulo) {
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: titulo,
    dates: `${dtStr(fecha, hora)}/${dtStrEnd(fecha, hora)}`,
    location: lugar,
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function buildOutlookLink(fecha, hora, lugar, titulo) {
  const [h, m] = hora.split(":").map(Number);
  const endH   = String((h + 1) % 24).padStart(2, "0");
  const p = new URLSearchParams({
    rru: "addevent",
    startdt: `${fecha}T${hora}:00`,
    enddt:   `${fecha}T${endH}:${String(m).padStart(2, "0")}:00`,
    subject: titulo,
    location: lugar,
    path: "/calendar/action/compose",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
}

function buildICS(fecha, hora, lugar, titulo, organizerEmail = "", attendeeEmail = "") {
  const uid  = `catelli-${fecha}-${hora.replace(":", "")}@inversiones-catelli.com`;
  const now  = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Inversiones Catelli//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:REQUEST",
    "BEGIN:VEVENT",
    `DTSTART:${dtStr(fecha, hora)}`,
    `DTEND:${dtStrEnd(fecha, hora)}`,
    `DTSTAMP:${now}`,
    `SUMMARY:${titulo}`,
    `LOCATION:${lugar}`,
    "STATUS:CONFIRMED",
    `UID:${uid}`,
  ];
  if (organizerEmail) lines.push(`ORGANIZER;CN=Inversiones Catelli:mailto:${organizerEmail}`);
  if (attendeeEmail)  lines.push(`ATTENDEE;CUTYPE=INDIVIDUAL;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE;CN=${attendeeEmail}:mailto:${attendeeEmail}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

function buildEmailHtml(asunto, body, calendarData = null) {
  const bodyHtml = body
    .split("\n")
    .map((l) => l.trim())
    .map((l) =>
      l
        ? `<p style="margin:0 0 16px 0;line-height:1.7;color:#37474f;font-size:15px;font-family:Arial,Helvetica,sans-serif">${esc(l)}</p>`
        : "<br>"
    )
    .join("");

  const calendarBlock = calendarData ? (() => {
    const { fecha, hora, lugar, titulo } = calendarData;
    const gLink = buildGoogleLink(fecha, hora, lugar, titulo);
    const oLink = buildOutlookLink(fecha, hora, lugar, titulo);
    return `
      <tr><td style="background-color:#fff;padding:0 36px 24px;border-left:1px solid #e8eaf6;border-right:1px solid #e8eaf6">
        <div style="border:2px solid #e8eaf6;border-radius:10px;padding:20px;text-align:center">
          <p style="margin:0 0 4px;font-family:Arial,sans-serif;font-size:13px;font-weight:700;color:#151C84">
            Agregar al calendario
          </p>
          <p style="margin:0 0 16px;font-family:Arial,sans-serif;font-size:12px;color:#9e9e9e">
            Guarda esta cita para no olvidarla
          </p>
          <table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto">
            <tr>
              <td style="padding:0 5px">
                <a href="${gLink}" target="_blank" style="display:inline-block;padding:10px 18px;background-color:#4285F4;color:#fff;font-family:Arial,sans-serif;font-size:12px;font-weight:700;text-decoration:none;border-radius:6px">
                  Google Calendar
                </a>
              </td>
              <td style="padding:0 5px">
                <a href="${oLink}" target="_blank" style="display:inline-block;padding:10px 18px;background-color:#0078D4;color:#fff;font-family:Arial,sans-serif;font-size:12px;font-weight:700;text-decoration:none;border-radius:6px">
                  Outlook
                </a>
              </td>
            </tr>
          </table>
          <p style="margin:14px 0 0;font-family:Arial,sans-serif;font-size:11px;color:#bdbdbd">
            El archivo .ics adjunto es compatible con Apple Calendar y Outlook de escritorio
          </p>
        </div>
      </td></tr>`;
  })() : "";


  const year = new Date().getFullYear();

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>${esc(asunto)}</title>
</head>
<body style="margin:0;padding:0;background-color:#f0f2f5;font-family:Arial,Helvetica,sans-serif">
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td align="center" style="padding:24px 16px">
      <table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%">

        <tr><td style="background:linear-gradient(135deg,#151C84 0%,#1a237e 100%);border-radius:12px 12px 0 0;padding:28px 36px">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td>
              <div style="font-family:Arial,sans-serif;color:#fff;font-size:12px;font-weight:700;letter-spacing:3px;text-transform:uppercase">INVERSIONES</div>
              <div style="font-family:Arial,sans-serif;color:#fff;font-size:26px;font-weight:900;letter-spacing:3px;line-height:1.1">CATELLI</div>
              <div style="font-family:Arial,sans-serif;color:#C81416;font-size:10px;font-weight:700;letter-spacing:2px;margin-top:6px;text-transform:uppercase">Gestión Humana</div>
            </td>
            <td align="right" valign="middle">
              <div style="width:52px;height:52px;background-color:#C81416;border-radius:10px;text-align:center;line-height:52px;display:inline-block">
                <span style="color:#fff;font-size:30px;font-weight:900;font-family:Arial,sans-serif">C</span>
              </div>
            </td>
          </tr></table>
        </td></tr>

        <tr><td style="background-color:#0d125c;padding:12px 36px">
          <p style="margin:0;color:#8c9ec4;font-size:12px;font-weight:600;letter-spacing:0.5px;font-family:Arial,sans-serif">${esc(asunto)}</p>
        </td></tr>

        <tr><td style="background-color:#fff;padding:36px;border-left:1px solid #e8eaf6;border-right:1px solid #e8eaf6">
          ${bodyHtml}
        </td></tr>

        ${calendarBlock}

        <tr><td style="background-color:#fff;padding:0 36px 20px;border-left:1px solid #e8eaf6;border-right:1px solid #e8eaf6">
          <div style="height:3px;background-color:#C81416;border-radius:2px;opacity:0.3"></div>
        </td></tr>

        <tr><td style="background-color:#f8f9fa;padding:20px 36px;border-radius:0 0 12px 12px;border:1px solid #e8eaf6;border-top:none">
          <p style="margin:0 0 4px;font-family:Arial,sans-serif;color:#9e9e9e;font-size:11px;line-height:1.6">
            <strong style="color:#757575">Inversiones Catelli</strong> &middot; Departamento de Gestión Humana
          </p>
          <p style="margin:0;font-family:Arial,sans-serif;color:#bdbdbd;font-size:10px;line-height:1.5">
            Mensaje generado automáticamente &mdash; No responda directamente a este correo &nbsp;|&nbsp; &copy; ${year} Inversiones Catelli
          </p>
        </td></tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function applyVars(text = "", candidate = {}, extra = {}) {
  return text
    .replace(/\{\{nombre\}\}/g,         candidate.nombre || "")
    .replace(/\{\{apellido\}\}/g,        candidate.apellido || "")
    .replace(/\{\{nombre_completo\}\}/g, `${candidate.nombre || ""} ${candidate.apellido || ""}`.trim())
    .replace(/\{\{correo\}\}/g,          candidate.correo || "")
    .replace(/\{\{telefono\}\}/g,        candidate.telefono || "")
    .replace(/\{\{puesto\}\}/g,          extra.puesto !== undefined ? extra.puesto : candidate.puesto || "")
    .replace(/\{\{empresa\}\}/g,         "Inversiones Catelli")
    .replace(/\{\{fecha\}\}/g,           extra.fecha || "")
    .replace(/\{\{hora\}\}/g,            extra.hora || "")
    .replace(/\{\{lugar\}\}/g,           extra.lugar || "");
}

function getPickerVars(text = "") {
  return VARS_PICKER.filter((v) => text.includes(`{{${v}}}`));
}

const todayISO = () => new Date().toISOString().split("T")[0];

// ── Component ─────────────────────────────────────────────────────────────────

export const ComunicacionesPanel = forwardRef(function ComunicacionesPanel(
  { documentoId, correoElectronico, nombre, apellido, telefono, puesto, accionActual, onAccionChange }, ref
) {
  const { accessToken, user } = useAuth();
  const { showAlert }   = useUI();

  const [comunicaciones, setComunicaciones] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [expandedId, setExpandedId]         = useState(null);

  // Template picker
  const [showPicker, setShowPicker]     = useState(false);
  const [pendingCanal, setPendingCanal] = useState(null);
  const [plantillas, setPlantillas]     = useState([]);
  const [loadingPlant, setLoadingPlant] = useState(false);

  // Variable completion modal
  const [showVarModal, setShowVarModal]             = useState(false);
  const [pendingPlantilla, setPendingPlantilla]     = useState(null);
  const [pickerVars, setPickerVars]                 = useState([]);
  const [needPuestoField, setNeedPuestoField]       = useState(false);
  const [extraVars, setExtraVars]                   = useState({
    puesto: "", fecha: todayISO(), hora: "",
    lugarTipo: "oficina", lugarTexto: OFICINA_DIR, lugarUrl: "",
  });

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm]           = useState({ asunto: "", mensaje: "" });
  const [sendingEmail, setSendingEmail]     = useState(false);

  // Externo compose (WA/TG)
  const [showExternoModal, setShowExternoModal] = useState(false);
  const [externoMsg, setExternoMsg]             = useState("");

  // Cambio de estado vinculado a la plantilla
  const [pendingAccionInfo, setPendingAccionInfo] = useState(null); // { accion, optional }
  const [showAccionStep, setShowAccionStep]       = useState(false);
  const [savingAccion, setSavingAccion]           = useState(false);

  // Cita a guardar tras el envío exitoso (fecha+hora+lugar del modal de variables)
  const [pendingCita, setPendingCita] = useState(null);

  const candidate = { nombre, apellido, correo: correoElectronico, telefono, puesto };

  useEffect(() => { fetchComunicaciones(); }, [documentoId]); // eslint-disable-line

  const fetchComunicaciones = async () => {
    try {
      setLoading(true);
      const data = await comunicacionAPI.getComunicaciones(accessToken, documentoId);
      setComunicaciones(data);
      setExpandedId(data[0]?.id ?? null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cleanPhone = (phone) => (phone || "").replace(/\D/g, "");

  const handleExternalMessage = async (appType, messageOverride, accion) => {
    const phoneNumber = cleanPhone(telefono);
    if (!phoneNumber) {
      showAlert("Atención", "El candidato no tiene un teléfono válido registrado.", "warning");
      return;
    }
    const msg = messageOverride || `Hola ${nombre || "candidato"}, te contactamos de Inversiones Catelli...`;
    const url = appType === "WHATSAPP"
      ? `https://wa.me/${phoneNumber}?text=${encodeURIComponent(msg)}`
      : `https://t.me/+${phoneNumber}`;
    try {
      await comunicacionAPI.logComunicacion(accessToken, {
        documentoId, canal: appType,
        mensaje: `Contacto iniciado vía ${appType}. Mensaje: ${msg}`,
        accion,
      });
      if (accion) onAccionChange?.(accion);
      fetchComunicaciones();
    } catch {}
    window.open(url, "_blank");
  };

  useImperativeHandle(ref, () => ({
    openEmail:    () => openPicker("EMAIL"),
    openWhatsApp: () => openPicker("WHATSAPP"),
    openTelegram: () => openPicker("TELEGRAM"),
  }));

  // ── Template picker ────────────────────────────────────────────────────────

  const openPicker = async (canal) => {
    setPendingCanal(canal);
    setShowPicker(true);
    setLoadingPlant(true);
    try {
      const all = await plantillaAPI.getAll(accessToken, { activo: true });
      setPlantillas(all.filter((p) => p.canal === canal || p.canal === "TODOS"));
    } catch {
      setPlantillas([]);
    } finally {
      setLoadingPlant(false);
    }
  };

  const continueWithTemplate = (plantilla) => {
    if (!plantilla) {
      if (pendingCanal === "EMAIL") {
        setEmailForm({ asunto: "", mensaje: "" });
        setShowEmailModal(true);
      } else {
        setExternoMsg(`Hola ${nombre || "candidato"}, te contactamos de Inversiones Catelli...`);
        setShowExternoModal(true);
      }
      return;
    }

    const fullText = (plantilla.asunto || "") + " " + (plantilla.contenido || "");
    const pvars    = getPickerVars(fullText);
    const needPuesto = fullText.includes("{{puesto}}");

    if (pvars.length > 0 || needPuesto) {
      setPendingPlantilla(plantilla);
      setPickerVars(pvars);
      setNeedPuestoField(needPuesto);
      setExtraVars({
        puesto: puesto || "",
        fecha: todayISO(),
        hora: "",
        lugarTipo: "oficina",
        lugarTexto: OFICINA_DIR,
        lugarUrl: "",
      });
      setShowVarModal(true);
    } else {
      commitTemplate(plantilla, {});
    }
  };

  const applyTemplate = (plantilla) => {
    setShowPicker(false);

    if (accionActual?.toLowerCase() === "contratado") {
      continueWithTemplate(plantilla);
      return;
    }

    // WhatsApp/Telegram nunca cambian estado — accion solo aplica a EMAIL
    const accionInfo = (pendingCanal === "EMAIL" && plantilla?.tipo) ? (TIPO_ACCION[plantilla.tipo] || null) : null;

    if (accionInfo?.optional) {
      // Para rechazo/banco: preguntar si solo cambiar estado o también enviar mensaje
      setPendingPlantilla(plantilla);
      setPendingAccionInfo(accionInfo);
      setShowAccionStep(true);
      return;
    }

    // Para los obligatorios: guardar accion y continuar con el flujo normal
    setPendingAccionInfo(accionInfo);
    continueWithTemplate(plantilla);
  };

  const handleSoloAccion = async () => {
    if (!pendingAccionInfo) return;
    const current = accionActual?.toLowerCase() || "nuevo";
    const validNext = VALID_TRANSITIONS_CP[current] || [];
    if (!validNext.includes(pendingAccionInfo.accion)) {
      showAlert("Atención", "Este cambio de estado no es posible desde el estado actual del candidato.", "warning");
      setShowAccionStep(false);
      setPendingAccionInfo(null);
      setPendingPlantilla(null);
      return;
    }
    setSavingAccion(true);
    try {
      await formularioAPI.updateFormularioAccion(accessToken, documentoId, { accion: pendingAccionInfo.accion });
      onAccionChange?.(pendingAccionInfo.accion);
      setShowAccionStep(false);
      setPendingAccionInfo(null);
      setPendingPlantilla(null);
      showAlert("Listo", `Candidato marcado como ${ACCION_LABEL[pendingAccionInfo.accion]}`, "success");
    } catch {
      showAlert("Error", "No se pudo actualizar el estado", "error");
    } finally {
      setSavingAccion(false);
    }
  };

  const handleEnviarConAccion = () => {
    setShowAccionStep(false);
    continueWithTemplate(pendingPlantilla);
  };

  // ── Variable modal helpers ─────────────────────────────────────────────────

  const setEv = (key, val) => setExtraVars((p) => ({ ...p, [key]: val }));

  const changeLugarTipo = (tipo) => {
    setExtraVars((p) => ({
      ...p, lugarTipo: tipo,
      lugarTexto: tipo === "oficina" ? OFICINA_DIR : "",
      lugarUrl: "",
    }));
  };

  const resolveExtra = (ev) => {
    const result = {
      fecha: ev.fecha ? formatFecha(ev.fecha) : "",
      hora:  ev.hora  ? formatHora(ev.hora)   : "",
      lugar:
        ev.lugarTipo === "oficina" ? (ev.lugarTexto || OFICINA_DIR) :
        ev.lugarTipo === "virtual" ? (ev.lugarUrl || "") :
        (ev.lugarTexto || ""),
    };
    if (ev.puesto !== undefined) result.puesto = ev.puesto;
    return result;
  };

  const canProceed = () => {
    if (pickerVars.includes("hora") && !extraVars.hora) return false;
    if (pickerVars.includes("lugar")) {
      if (extraVars.lugarTipo === "virtual" && !extraVars.lugarUrl.trim()) return false;
      if (extraVars.lugarTipo === "custom"  && !extraVars.lugarTexto.trim()) return false;
    }
    return true;
  };

  // ── Commit template → open compose modal ──────────────────────────────────

  const commitTemplate = (plantilla, ev) => {
    setShowVarModal(false);
    const extra = resolveExtra(ev);

    // Si el template tiene fecha+hora, preparar la cita para guardar tras el envío
    const isCita = ev.fecha && ev.hora;
    let citaLocal = null;
    if (isCita) {
      const titulo = plantilla?.tipo === "bienvenida"
        ? "Primer día en Inversiones Catelli"
        : plantilla?.tipo === "prueba_tecnica"
        ? "Evaluación técnica — Inversiones Catelli"
        : "Entrevista en Inversiones Catelli";
      citaLocal = {
        fecha: ev.fecha,
        hora: ev.hora,
        lugar: extra.lugar,
        tipo: inferTipoCita(plantilla?.tipo),
        titulo,
      };
      setPendingCita(citaLocal);
    } else {
      setPendingCita(null);
    }

    const msg = applyVars(plantilla.contenido, candidate, extra);
    if (pendingCanal === "EMAIL") {
      setEmailForm({ asunto: applyVars(plantilla.asunto || "", candidate, extra), mensaje: msg });
      setShowEmailModal(true);
    } else {
      // WhatsApp/Telegram: solo URL de Google Calendar, sin .ics ni Accept/Decline
      let finalMsg = msg;
      if (citaLocal) {
        const gLink = buildGoogleLink(ev.fecha, ev.hora, extra.lugar, citaLocal.titulo);
        finalMsg = `${msg}\n\nAgrega al calendario:\n${gLink}`;
      }
      setExternoMsg(finalMsg);
      setShowExternoModal(true);
    }
  };

  // ── Send email (HTML wrapper) ──────────────────────────────────────────────

  const handleSendEmail = async () => {
    if (!emailForm.asunto || !emailForm.mensaje) {
      showAlert("Atención", "Asunto y mensaje son requeridos", "warning");
      return;
    }
    setSendingEmail(true);
    try {
      // Preparar datos de calendario si corresponde
      const calendarData = pendingCita
        ? { fecha: pendingCita.fecha, hora: pendingCita.hora, lugar: pendingCita.lugar, titulo: pendingCita.titulo }
        : null;

      const icsBase64 = calendarData
        ? btoa(unescape(encodeURIComponent(
            buildICS(
              calendarData.fecha,
              calendarData.hora,
              calendarData.lugar,
              calendarData.titulo,
              user?.email || "",
              correoElectronico || "",
            )
          )))
        : undefined;

      // Validar que la transición de estado sea permitida antes de aplicarla
      const rawAccion = pendingAccionInfo?.accion || undefined;
      const current = accionActual?.toLowerCase() || "nuevo";
      const validNext = VALID_TRANSITIONS_CP[current] || [];
      const accionToApply = rawAccion && validNext.includes(rawAccion) ? rawAccion : undefined;

      await comunicacionAPI.sendEmail(accessToken, {
        documentoId,
        asunto:      emailForm.asunto,
        mensaje:     emailForm.mensaje,
        htmlMensaje: buildEmailHtml(emailForm.asunto, emailForm.mensaje, calendarData),
        accion:      accionToApply,
        icsBase64,
      });

      if (accionToApply) onAccionChange?.(accionToApply);

      // Guardar la cita en BD de forma silenciosa
      if (pendingCita) {
        citaAPI.create(accessToken, documentoId, {
          tipo:  pendingCita.tipo,
          fecha: pendingCita.fecha,
          hora:  pendingCita.hora,
          lugar: pendingCita.lugar,
        }).catch(() => {});
        setPendingCita(null);
      }

      setShowEmailModal(false);
      setEmailForm({ asunto: "", mensaje: "" });
      setPendingAccionInfo(null);
      showAlert("Éxito", "Correo enviado correctamente", "success");
      fetchComunicaciones();
    } catch (error) {
      showAlert("Error", error.message || "No se pudo enviar el correo", "error");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendExterno = () => {
    setShowExternoModal(false);
    setPendingAccionInfo(null); // WhatsApp/Telegram nunca cambian estado

    // Guardar cita silenciosamente si había datos de fecha+hora
    if (pendingCita) {
      citaAPI.create(accessToken, documentoId, {
        tipo:  pendingCita.tipo,
        fecha: pendingCita.fecha,
        hora:  pendingCita.hora,
        lugar: pendingCita.lugar,
      }).catch(() => {});
      setPendingCita(null);
    }

    handleExternalMessage(pendingCanal, externoMsg, undefined);
  };

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">

      {/* Panel header */}
      <div className="bg-gray-50 border-b border-gray-100 p-4 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span>📞</span> Centro de Comunicaciones
          </h2>
          <p className="text-sm text-gray-500 mt-1">Registra y gestiona el contacto con este candidato.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {["EMAIL", "WHATSAPP", "TELEGRAM"].map((canal) => (
            <button
              key={canal}
              onClick={() => openPicker(canal)}
              className={`px-4 py-2 ${CANAL_COLOR[canal]} text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2 shadow-sm`}
            >
              <span>{CANAL_ICON[canal]}</span>
              {canal === "EMAIL" ? "Correo" : canal === "WHATSAPP" ? "WhatsApp" : "Telegram"}
            </button>
          ))}
        </div>
      </div>

      {/* Communication history — Accordion */}
      <div className="p-4 md:p-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-[#151C84]/20 border-t-[#151C84] rounded-full animate-spin" />
          </div>
        ) : comunicaciones.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <span className="text-4xl mb-3 block">📭</span>
            <p>Aún no hay comunicaciones registradas con este candidato.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {comunicaciones.map((com) => {
              const isOpen = expandedId === com.id;
              const canalLabel = com.canal === "EMAIL" ? "Correo" : com.canal === "WHATSAPP" ? "WhatsApp" : "Telegram";
              const dotColor =
                com.estado === "ERROR"    ? "bg-red-500"    :
                com.canal === "EMAIL"     ? "bg-[#151C84]"  :
                com.canal === "WHATSAPP"  ? "bg-[#25D366]"  : "bg-[#0088cc]";

              return (
                <div key={com.id} className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  isOpen ? "border-[#151C84]/20 shadow-sm" : "border-gray-100 hover:border-gray-200"
                }`}>
                  {/* Accordion header */}
                  <button
                    onClick={() => setExpandedId(isOpen ? null : com.id)}
                    className="w-full flex items-center gap-3 p-3.5 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />

                    <span className="text-sm font-semibold text-gray-800 flex-shrink-0">
                      {CANAL_ICON[com.canal]} {canalLabel}
                    </span>

                    {com.asunto ? (
                      <span className="text-xs text-[#151C84] font-medium truncate flex-1">
                        {com.asunto}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 truncate flex-1 italic">
                        {com.mensaje?.substring(0, 60)}{com.mensaje?.length > 60 ? "…" : ""}
                      </span>
                    )}

                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      <span className="text-xs text-gray-400 hidden sm:block">
                        {com.usuario?.name || "Admin"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(com.createdAt).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "2-digit" })}
                      </span>
                      {com.estado === "ERROR" && (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">ERROR</span>
                      )}
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Accordion body */}
                  {isOpen && (
                    <div className="px-4 py-4 bg-white border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
                        <span>Por: <strong className="text-gray-600">{com.usuario?.name || "Admin"}</strong></span>
                        <span>·</span>
                        <span>{new Date(com.createdAt).toLocaleString("es-DO")}</span>
                      </div>
                      {com.asunto && (
                        <p className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-50">
                          {com.asunto}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Segoe UI Emoji','Apple Color Emoji','Noto Color Emoji',sans-serif" }}>{com.mensaje}</p>
                      {com.estado === "ERROR" && (
                        <p className="text-xs text-red-600 mt-3 font-bold">⚠️ Falló el envío de este mensaje.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Accion Step Modal (solo para tipos opcionales: descarte / banco) ── */}
      <ModalCustom isVisible={showAccionStep} onClose={() => { setShowAccionStep(false); setPendingAccionInfo(null); setPendingPlantilla(null); }}>
        <div className="p-6 space-y-5" style={{ minWidth: 360 }}>
          <div className="border-b border-gray-100 pb-3">
            <h3 className="text-lg font-bold text-gray-900">{pendingPlantilla?.nombre}</h3>
            <p className="text-sm text-gray-500 mt-0.5">¿Cómo deseas proceder con este candidato?</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <span className="text-lg flex-shrink-0">⚠️</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">
                El candidato pasará a estado: <strong>{ACCION_LABEL[pendingAccionInfo?.accion]}</strong>
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Puedes actualizar solo el estado interno, o también enviar la notificación al candidato.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleSoloAccion}
              disabled={savingAccion}
              className="w-full px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-xl transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {savingAccion ? (
                <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Guardando...</>
              ) : (
                <><span>🔒</span> Solo cambiar estado (sin notificar)</>
              )}
            </button>
            <button
              onClick={handleEnviarConAccion}
              disabled={savingAccion}
              className="w-full px-5 py-3 bg-[#151C84] hover:bg-[#0d125c] text-white font-bold rounded-xl transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <span>✉️</span> Enviar notificación al candidato
            </button>
          </div>

          <div className="flex justify-center pt-1">
            <button
              onClick={() => { setShowAccionStep(false); setPendingAccionInfo(null); setPendingPlantilla(null); }}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </ModalCustom>

      {/* ── Template Picker Modal ── */}
      <ModalCustom isVisible={showPicker} onClose={() => setShowPicker(false)}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Seleccionar Plantilla</h3>
              <p className="text-sm text-gray-400 mt-0.5">
                {CANAL_ICON[pendingCanal]} {pendingCanal === "EMAIL" ? "Correo electrónico" : pendingCanal === "WHATSAPP" ? "WhatsApp" : "Telegram"}
              </p>
            </div>
          </div>

          {loadingPlant ? (
            <div className="flex justify-center py-8">
              <div className="w-7 h-7 border-2 border-[#151C84]/20 border-t-[#151C84] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              <button
                onClick={() => applyTemplate(null)}
                className="w-full text-left p-3 rounded-xl border border-dashed border-gray-200 hover:border-[#151C84]/40 hover:bg-[#151C84]/3 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-[#151C84]/10 transition-colors flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Sin plantilla</p>
                    <p className="text-xs text-gray-400">Escribir mensaje manualmente</p>
                  </div>
                </div>
              </button>

              {plantillas.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-4">
                  No hay plantillas activas para este canal.<br />
                  <span className="text-xs">Ve a <strong>Plantillas</strong> en el menú para crearlas.</span>
                </p>
              )}

              {plantillas.map((p) => {
                const accionInfo = p.tipo ? (TIPO_ACCION[p.tipo] || null) : null;
                return (
                  <button
                    key={p.id}
                    onClick={() => applyTemplate(p)}
                    className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-[#151C84]/40 hover:bg-[#151C84]/3 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#151C84]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#151C84]/15 transition-colors">
                        <span className="text-base">{CANAL_ICON[p.canal] || "📄"}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.nombre}</p>
                          {accionInfo && (
                            <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              accionInfo.optional
                                ? "bg-amber-50 text-amber-700 border border-amber-200"
                                : "bg-[#151C84]/10 text-[#151C84] border border-[#151C84]/20"
                            }`}>
                              → {ACCION_LABEL[accionInfo.accion]}
                            </span>
                          )}
                        </div>
                        {p.asunto && (
                          <p className="text-xs text-[#151C84] font-medium truncate">{p.asunto}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{p.contenido}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
            <button onClick={() => setShowPicker(false)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-semibold rounded-xl transition-all">
              Cancelar
            </button>
          </div>
        </div>
      </ModalCustom>

      {/* ── Variable Completion Modal ── */}
      <ModalCustom
        isVisible={showVarModal}
        onClose={() => { setShowVarModal(false); setShowPicker(true); }}
      >
        <div className="p-6 space-y-5" style={{ minWidth: 380 }}>
          {/* Header */}
          <div className="border-b border-gray-100 pb-3">
            <h3 className="text-lg font-bold text-gray-900">Completar plantilla</h3>
            <p className="text-sm text-gray-400 mt-0.5">
              {CANAL_ICON[pendingCanal]} <span className="font-medium text-gray-600">{pendingPlantilla?.nombre}</span>
            </p>
          </div>

          {/* Puesto */}
          {needPuestoField && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                💼 Puesto al que aplica
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
                placeholder="Ej. Vendedor Externo, Mercaderista..."
                value={extraVars.puesto}
                onChange={(e) => setEv("puesto", e.target.value)}
              />
            </div>
          )}

          {/* Fecha */}
          {pickerVars.includes("fecha") && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                📅 Fecha de la cita / entrevista
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
                value={extraVars.fecha}
                onChange={(e) => setEv("fecha", e.target.value)}
              />
              {extraVars.fecha && (
                <p className="text-xs text-[#151C84] mt-1.5 pl-1 font-medium">
                  {formatFecha(extraVars.fecha)}
                </p>
              )}
            </div>
          )}

          {/* Hora */}
          {pickerVars.includes("hora") && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                🕐 Hora <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
                value={extraVars.hora}
                onChange={(e) => setEv("hora", e.target.value)}
              />
              {extraVars.hora && (
                <p className="text-xs text-[#151C84] mt-1.5 pl-1 font-medium">
                  {formatHora(extraVars.hora)}
                </p>
              )}
              {!extraVars.hora && (
                <p className="text-xs text-amber-500 mt-1 pl-1">Requerido</p>
              )}
            </div>
          )}

          {/* Lugar */}
          {pickerVars.includes("lugar") && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                📍 Lugar / Enlace <span className="text-red-500">*</span>
              </label>

              {/* Radio buttons */}
              <div className="flex gap-2 mb-3">
                {[
                  { key: "oficina", icon: "🏢", label: "Oficina" },
                  { key: "virtual", icon: "💻", label: "Videollamada" },
                  { key: "custom",  icon: "✏️", label: "Otro" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => changeLugarTipo(key)}
                    className={`flex-1 py-2 px-2 rounded-xl border text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                      extraVars.lugarTipo === key
                        ? "border-[#151C84] bg-[#151C84] text-white shadow-sm"
                        : "border-gray-200 text-gray-600 hover:border-[#151C84]/40 hover:bg-[#151C84]/5"
                    }`}
                  >
                    <span>{icon}</span>{label}
                  </button>
                ))}
              </div>

              {/* Oficina */}
              {extraVars.lugarTipo === "oficina" && (
                <div className="space-y-2">
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
                    placeholder="Dirección de la oficina..."
                    value={extraVars.lugarTexto}
                    onChange={(e) => setEv("lugarTexto", e.target.value)}
                  />
                  {extraVars.lugarTexto && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(extraVars.lugarTexto)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#151C84] hover:underline inline-flex items-center gap-1 pl-1"
                    >
                      🗺 Ver en Google Maps
                    </a>
                  )}
                </div>
              )}

              {/* Videollamada */}
              {extraVars.lugarTipo === "virtual" && (
                <div className="space-y-1">
                  <input
                    type="url"
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
                    placeholder="https://meet.google.com/xxx  ·  Zoom  ·  Teams..."
                    value={extraVars.lugarUrl}
                    onChange={(e) => setEv("lugarUrl", e.target.value)}
                  />
                  {!extraVars.lugarUrl && (
                    <p className="text-xs text-amber-500 pl-1">Ingresa el enlace de la videollamada</p>
                  )}
                </div>
              )}

              {/* Personalizado */}
              {extraVars.lugarTipo === "custom" && (
                <div className="space-y-1">
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
                    placeholder="Dirección, indicaciones o enlace..."
                    value={extraVars.lugarTexto}
                    onChange={(e) => setEv("lugarTexto", e.target.value)}
                  />
                  {!extraVars.lugarTexto && (
                    <p className="text-xs text-amber-500 pl-1">Requerido</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <button
              onClick={() => { setShowVarModal(false); setShowPicker(true); }}
              className="text-xs text-gray-400 hover:text-[#151C84] transition-colors font-medium"
            >
              ← Cambiar plantilla
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => setShowVarModal(false)}
                className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => commitTemplate(pendingPlantilla, extraVars)}
                disabled={!canProceed()}
                className="px-5 py-2.5 bg-[#151C84] text-white rounded-xl hover:bg-[#0d125c] font-bold transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Continuar →
              </button>
            </div>
          </div>
        </div>
      </ModalCustom>

      {/* ── Email Compose Modal ── */}
      <ModalCustom isVisible={showEmailModal} onClose={() => !sendingEmail && setShowEmailModal(false)}>
        <div className="p-6 space-y-4">
          <div className="border-b border-gray-100 pb-3">
            <h3 className="text-xl font-bold text-gray-900">Enviar Correo</h3>
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <span>✨</span> Se enviará con formato HTML profesional
            </p>
          </div>

          {pendingAccionInfo && !pendingAccionInfo.optional && (
            <div className="bg-[#151C84]/5 border border-[#151C84]/20 rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-base flex-shrink-0">🔄</span>
              <p className="text-sm text-[#151C84] font-medium">
                Al enviar, el candidato pasará a estado <strong>{ACCION_LABEL[pendingAccionInfo.accion]}</strong>
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Para</label>
            <input type="text"
              className="w-full px-4 py-2 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed outline-none text-sm"
              value={correoElectronico || "No tiene correo registrado"} disabled />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Asunto</label>
            <input type="text"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none text-sm"
              placeholder="Ej. Invitación a entrevista"
              value={emailForm.asunto}
              onChange={(e) => setEmailForm({ ...emailForm, asunto: e.target.value })} />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Mensaje</label>
            <textarea rows={8}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none resize-none text-sm"
              placeholder="Escribe el mensaje aquí..."
              value={emailForm.mensaje}
              onChange={(e) => setEmailForm({ ...emailForm, mensaje: e.target.value })} />
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <button
              onClick={() => { setShowEmailModal(false); setShowPicker(true); }}
              disabled={sendingEmail}
              className="text-xs text-gray-400 hover:text-[#151C84] transition-colors font-medium"
            >
              ← Cambiar plantilla
            </button>
            <div className="flex gap-3">
              <button onClick={() => setShowEmailModal(false)} disabled={sendingEmail}
                className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors text-sm">
                Cancelar
              </button>
              <button onClick={handleSendEmail} disabled={sendingEmail}
                className="px-5 py-2.5 bg-[#151C84] text-white rounded-xl hover:bg-[#0d125c] font-bold transition-colors flex items-center gap-2 text-sm">
                {sendingEmail ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Enviando...</>
                ) : (
                  <><span>✉️</span> Enviar Correo</>
                )}
              </button>
            </div>
          </div>
        </div>
      </ModalCustom>

      {/* ── WhatsApp / Telegram Compose Modal ── */}
      <ModalCustom isVisible={showExternoModal} onClose={() => setShowExternoModal(false)}>
        <div className="p-6 space-y-4">
          <h3 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2">
            {CANAL_ICON[pendingCanal]} Mensaje vía {pendingCanal === "WHATSAPP" ? "WhatsApp" : "Telegram"} a {nombre}
          </h3>

          {pendingAccionInfo && !pendingAccionInfo.optional && (
            <div className="bg-[#151C84]/5 border border-[#151C84]/20 rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-base flex-shrink-0">🔄</span>
              <p className="text-sm text-[#151C84] font-medium">
                Al enviar, el candidato pasará a estado <strong>{ACCION_LABEL[pendingAccionInfo.accion]}</strong>
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Mensaje</label>
            <textarea rows={8}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] transition-all outline-none resize-none text-sm"
              style={{ fontFamily: "'Segoe UI Emoji','Apple Color Emoji','Noto Color Emoji',sans-serif" }}
              value={externoMsg}
              onChange={(e) => setExternoMsg(e.target.value)} />
          </div>
          <p className="text-xs text-gray-400">Se abrirá la aplicación con este mensaje pre-cargado. El mensaje también quedará registrado en el historial.</p>
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <button
              onClick={() => { setShowExternoModal(false); setShowPicker(true); }}
              className="text-xs text-gray-400 hover:text-[#151C84] transition-colors font-medium"
            >
              ← Cambiar plantilla
            </button>
            <div className="flex gap-3">
              <button onClick={() => setShowExternoModal(false)}
                className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors text-sm">
                Cancelar
              </button>
              <button onClick={handleSendExterno}
                className={`px-5 py-2.5 text-white rounded-xl font-bold transition-colors flex items-center gap-2 text-sm ${
                  pendingCanal === "WHATSAPP" ? "bg-[#25D366] hover:bg-[#128C7E]" : "bg-[#0088cc] hover:bg-[#0077b5]"
                }`}>
                <span>{CANAL_ICON[pendingCanal]}</span> Abrir {pendingCanal === "WHATSAPP" ? "WhatsApp" : "Telegram"}
              </button>
            </div>
          </div>
        </div>
      </ModalCustom>

    </div>
  );
});
